import {
  GridColDef,
  GridRenderCellParams,
  GridInitialState,
} from "@mui/x-data-grid-premium";
import React from "react";
import { Link } from "react-router-dom";
import useApi from "../../hooks/useGaneshApi";

import { Customer_T } from "../../types/Customer";
import { formatDateYYYYMMDD, formatNumber } from "utils/formatting";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";

import { useError } from "../../contexts/ErrorContext";
import { useQuery } from "@tanstack/react-query";

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [
        {
          field: "customer_status",
          operator: "is",
          value: "Current",
        },
      ],
    },
  },
  sorting: { sortModel: [{ field: "id", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 50, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
  aggregation: {
    model: {
      name: "size",
      count_samples: "sum",
      count_active_deliveries: "sum",
      most_recent_volume: "sum",
      count_active_fg: "sum",
    },
  },
};

const CustomerSummaryTable: React.FC = () => {
  const { apiRequest } = useApi();
  const { showError } = useError();

  const {
    data: customerSummaryData = [],
    isLoading,
    isError,
    error,
  } = useQuery<Customer_T[], Error>({
    queryKey: ["customers"],
    queryFn: async () => {
      const response = await apiRequest<Customer_T[]>("/v1/customers/", {
        method: "GET",
      });
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
  });

  if (isError) {
    showError(`Failed to fetch customers list: ${error.message}`);
  }

  const columns_def: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
      renderCell: (params: GridRenderCellParams<Number>) => (
        <Link to={`/customer/${params.value}`}>{params.value}</Link>
      ),
    },
    {
      field: "name",
      headerName: "Customer",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link to={`/customer/${params.row.id}`}>{params.value}</Link>
      ),
    },

    {
      field: "all_samples_count",
      headerName: "All Time Samples",
      type: "number",
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
    {
      field: "active_delivery_count",
      headerName: "Active Deliveries",
      type: "number",
      width: 200,
      align: "center",
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
    {
      field: "most_recent_volume",
      headerName: "Most Recent Total Volume",
      type: "number",
      width: 200,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
    {
      field: "active_fg_count",
      headerName: "Active Fulfillment Groups",
      type: "number",
      width: 150,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
    {
      field: "fulfillment_destination_type",
      headerName: "Fulfillment Destination",
      width: 200,
    },
    {
      field: "delivery_data_format",
      headerName: "Data Format",
      width: 100,
    },
    {
      field: "most_recent_delivery",
      headerName: "Most Recent Delivery Date",
      type: "date",
      width: 200,
      valueGetter: (value) => value && new Date(value),
      renderCell: (params: GridRenderCellParams) =>
        params.value ? formatDateYYYYMMDD(new Date(params.value)) : "",
    },
    {
      field: "next_scheduled_delivery",
      headerName: "Next Scheduled Delivery Date",
      type: "date",
      width: 250,
      valueGetter: (value) => value && new Date(value),
      renderCell: (params: GridRenderCellParams) =>
        params.value ? formatDateYYYYMMDD(new Date(params.value)) : "",
    },
    {
      field: "next_license_end_date",
      headerName: "Next License End Date",
      type: "date",
      width: 200,
      valueGetter: (value) => value && new Date(value),
      renderCell: (params: GridRenderCellParams) =>
        params.value ? formatDateYYYYMMDD(new Date(params.value)) : "",
    },
    {
      field: "customer_status",
      headerName: "Status",
      description: "Whether customer has currently active deliveries",
      type: "singleSelect",
      width: 150,
      valueOptions: [
        { value: "Current", label: "Current" },
        { value: "Legacy", label: "Legacy" },
        { value: "Prospect", label: "Prospect" },
      ],
    },
  ];

  return (
    <GaneshDataGrid
      loading={isLoading}
      data={customerSummaryData}
      columns={columns_def}
      gridId="customerSummaryGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default CustomerSummaryTable;
